import type { ReactNode } from "react";
import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { AGGREGATION } from "@shared/scorecard";
import type { ClaimVerdict } from "../../../../types/claims";
import { Caption } from "../../../primitives/data-display/Caption";

interface VerdictScaleProps {
  verdict: ClaimVerdict;
  /** Aggregated signal on the roughly [-1, 1] contradicted-to-supported axis. */
  signal: number;
  /** Shown between the track and its end labels, so the labels also name what it plots. */
  children?: ReactNode;
}

const { strong, lean } = AGGREGATION.thresholds;

/** Zone boundaries mirror the real verdict thresholds, so the bands are never arbitrary. */
const ZONES: { from: number; to: number; verdict: ClaimVerdict }[] = [
  { from: -1, to: -strong, verdict: "CONTRADICTED" },
  { from: -strong, to: -lean, verdict: "LEANING_CONTRADICTED" },
  { from: -lean, to: lean, verdict: "MIXED" },
  { from: lean, to: strong, verdict: "LEANING_SUPPORTED" },
  { from: strong, to: 1, verdict: "SUPPORTED" },
];

function toPercent(signal: number): number {
  const clamped = Math.max(-1, Math.min(1, signal));
  return ((clamped + 1) / 2) * 100;
}

const ScaleTrackWrapper = styled("div")({
  position: "relative",
});

const ScaleTrack = styled("div")({
  display: "flex",
  height: 8,
  borderRadius: 4,
  overflow: "hidden",
});

const ScaleZone = styled("div")<{
  $width: number;
  $verdict: ClaimVerdict;
  $noStance: boolean;
}>(({ theme, $width, $verdict, $noStance }) => ({
  flexBasis: `${$width}%`,
  backgroundColor:
    theme.verdicts[$noStance && $verdict === "MIXED" ? "INSUFFICIENT" : $verdict].main,
  opacity: 0.3,
}));

const ScaleMarker = styled("div")<{
  $left: number;
  $verdict: ClaimVerdict;
  $hollow: boolean;
}>(({ theme, $left, $verdict, $hollow }) => {
  const color = $hollow ? theme.verdicts.INSUFFICIENT.main : theme.verdicts[$verdict].main;
  return {
    position: "absolute",
    top: "50%",
    left: `${$left}%`,
    transform: "translate(-50%, -50%)",
    width: 16,
    height: 16,
    borderRadius: "50%",
    backgroundColor: $hollow ? theme.palette.background.paper : color,
    border: `2.5px solid ${color}`,
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
  };
});

const ScaleExtra = styled("div")(({ theme }) => ({
  marginTop: theme.spacing(1.5),
}));

const ScaleLabels = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  marginTop: theme.spacing(0.75),
}));

/**
 * A single directional scale from Contradicted to Supported, positioned by the claim's
 * real aggregated signal. Replaces per-category labels (which crowd on narrow screens)
 * with one marker on a continuous axis — the verdict word above already names the category.
 */
export function VerdictScale({ verdict, signal, children }: VerdictScaleProps) {
  const { t } = useTranslation();

  // INSUFFICIENT and INCONCLUSIVE are not stances, so the marker reads as neutral rather than
  // borrowing a zone color it may not actually sit in (e.g. thin evidence with a strong raw signal).
  const hasNoStance = verdict === "INSUFFICIENT" || verdict === "INCONCLUSIVE";

  return (
    <>
      <ScaleTrackWrapper>
        <ScaleTrack>
          {ZONES.map((zone) => (
            <ScaleZone
              key={zone.verdict}
              $width={((zone.to - zone.from) / 2) * 100}
              $verdict={zone.verdict}
              $noStance={hasNoStance}
            />
          ))}
        </ScaleTrack>
        <ScaleMarker
          aria-hidden
          $left={toPercent(signal)}
          $verdict={verdict}
          $hollow={hasNoStance}
        />
      </ScaleTrackWrapper>
      {children && <ScaleExtra>{children}</ScaleExtra>}
      {/* Ends only — no center label. A "Mixed" tick here would sit directly under the
          marker for any near-zero signal (including most INCONCLUSIVE claims), reading as
          if it named the verdict. The zone color and the verdict text above already cover it. */}
      <ScaleLabels>
        <Caption>{t("verdicts.CONTRADICTED")}</Caption>
        <Caption>{t("verdicts.SUPPORTED")}</Caption>
      </ScaleLabels>
    </>
  );
}
